import { useEffect, useRef } from 'react'
import { rfStore } from '../lib/rfstore'
import { useI18n } from '../lib/i18n'
import { nivelParaCor, parseSUnits } from '../lib/sinalCores'

/**
 * Leitura de sinal: S-meter, legibilidade e SNR em texto.
 *
 * Existe como componente próprio porque isto estava no JSX do App lendo
 * `state.rx` — e `setRX` NÃO notifica o React de propósito, já que é o caminho
 * quente de 10 Hz. O resultado é que o texto nunca atualizava: o operador via
 * "canal silencioso" enquanto o sinal chegava, e concluía que o áudio estava
 * quebrado.
 *
 * Mesmo padrão do SMeter: renderiza uma vez e se atualiza por
 * requestAnimationFrame, escrevendo direto no nó.
 */
/** R2 ou pior: sinal tecnicamente presente, mas mal dá pra entender — é o
 * ponto de acionar a dica de equipamento (ver aviso abaixo). */
const LIMIAR_SINAL_FRACO = 2

export function RXReadout() {
  const { t } = useI18n()
  const ref = useRef<HTMLSpanElement>(null)
  const avisoRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const el = ref.current
    const aviso = avisoRef.current
    if (!el || !aviso) return

    let raf = 0
    let ultimo = ''
    let avisoVisivel = false

    const tick = () => {
      const s = rfStore.snapshot()
      const rx = rfStore.isReceiving(performance.now()) ? s.rx : null
      const texto = rx
        ? `${rx.s_meter} · R${rx.readability} · SNR ${rx.snr_db.toFixed(0)} dB`
        : t.dock.canalSilencioso

      // Só toca no DOM quando muda: escrever textContent a 60 fps força
      // recálculo de layout à toa.
      if (texto !== ultimo) {
        el.textContent = texto
        // Tinge o próprio texto pela força do sinal — o único mostrador de
        // sinal que hoje não tinha nenhuma pista visual além do número.
        el.style.color = rx ? nivelParaCor(parseSUnits(rx.s_meter) / 12) : ''
        ultimo = texto
      }

      // Sinal fraco não é mais motivo pra ficar mudo (ver aplicarGate em
      // audio.ts) — só motivo pra soar ruim. Esta dica é o porquê, pra não
      // parecer bug: antena, potência e distância continuam importando.
      const mostrarAviso = !!rx && rx.readability <= LIMIAR_SINAL_FRACO
      if (mostrarAviso !== avisoVisivel) {
        aviso.style.display = mostrarAviso ? '' : 'none'
        avisoVisivel = mostrarAviso
      }

      raf = requestAnimationFrame(tick)
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [t])

  return (
    <>
      <span ref={ref}>{t.dock.canalSilencioso}</span>
      <span ref={avisoRef} className="dock-sinal-fraco" style={{ display: 'none' }}>
        {t.dock.sinalFraco}
      </span>
    </>
  )
}
